import { ChangeDetectionStrategy, Component, HostListener, inject } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { KsizePipe } from '@pipes';


import { OverlayRef } from '@angular/cdk/overlay';

import { SM, stopEvent, Tag, VzFile, VzFileContentType } from '@models';
import { PublicApiService } from '@services/api.service';
import { BaseMin } from '@utils/base-min';
import { ErrorMessageComponent } from '@utils/error-message.component';

@Component({
    selector: 'vz-image-viewer',
    templateUrl: './image-viewer.component.html',
    styleUrls: ['./image-viewer.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DragDropModule,
        KsizePipe, ErrorMessageComponent
    ]
})
@Tag('ImageViewerComponent')
export class ImageViewerComponent extends BaseMin {
    _hostFit = true;
    _hostLayout = false;
    @HostListener('window:keydown', ['$event'])
    globalTouch(e: KeyboardEvent): void {
        if (e.key == 'Escape' && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey && this.overlayRef) {
            this.overlayRef.dispose();
            e.stopImmediatePropagation();
            e.preventDefault();
        }
    }

    overlayRef?: OverlayRef;
    files?: (VzFile | undefined)[];
    previews: SM<SafeUrl> = {};
    images: SM<SafeUrl> = {};
    videos: SM<string | undefined> = {};
    index: number = -1;
    err: boolean = false;
    justmoved: boolean = true;
    justmovedTimer: any;
    scale: number = 1;
    rotate: number = 0;
    pos = { x: 0, y: 0 };
    dragged: boolean = false;

    trunc = Math.trunc;
    se = stopEvent;

    private readonly _api       = inject(PublicApiService);

    showImages(files: VzFile[], index: number, overlayRef: OverlayRef): void {
        this.previews = {};
        this.images = {};
        this.index = index;
        this.files = files;
        this.overlayRef = overlayRef;
        this.loadImage(index);
    }

    loadImage(ind: number): void {
        this.scale = 1;
        this.rotate = 0;
        if (ind >= 0 && this.files && this.files.length > ind) {
            this.index = ind;
            const fid = this.files[ind]?.id;
            if (fid) {
                this._loading.set(true);
                if (this.files[ind]?.metadata?.type == VzFileContentType.Video) {
                    this._S.req = this._api.getFileDownloadLink(fid).subscribe({
                        next: res => {
                            this.videos[fid] = res?.url;
                            this._loading.set(false);
                            this._Changed();
                        },
                        error: err => this._Error(err)
                    });
                }
                else {
                    this._S.req = this._api.getFileDownloadLink(fid).subscribe({
                        next: res => {
                            this.images[fid] = res?.url;
                            this._loading.set(false);
                            this._Changed();
                        },
                        error: err => this._Error(err)
                    });
                }
            }
        }
    }

    nextImage(): void {
        if (this.files && this.index > -1 && this.index < this.files.length) {
            this.loadImage(this.index + 1);
        }
    }

    prevImage(): void {
        if (this.files && this.index > 0) {
            this.loadImage(this.index - 1);
        }
    }

    moved(): void {
        if (!this.justmoved) {
            this._Changed();
        }
        this.justmoved = true;
        if (this.justmovedTimer) {
            clearTimeout(this.justmovedTimer);
        }
        this.justmovedTimer = setTimeout(() => {
            this.justmoved = false;
            this._Changed();
        }, 2000);
    }

    turn(angle: number): void {
        this.rotate += angle;
        this._Changed();
    }

    zoom(scale: number): void {
        this.setScale(this.scale + scale);
    }

    setScale(scale: number): void {
        if (scale < .15) {
            scale = .15;
        }
        if (scale > 3) {
            scale = 3;
        }
        this.scale = scale;
        this._Changed();
    }

    onPinch(e: any): void {
        this.zoom(this.scale * e.scale);
    }

    scroll(e: any): void {
        this.moved();
        if (!this._loading() && !this._error() && e?.deltaY) {
            this.zoom(e.deltaY / 1000);
        }
    }

    reset(): void {
        this.scale = 1;
        this.rotate = 0;
        this.dragged = false;
        this.pos = { x: 0, y: 0 };
    }

    shrinkToFit(img: HTMLImageElement | HTMLVideoElement, root: HTMLDivElement): void {
        try {
            const rb = root.getBoundingClientRect();
            const hs = rb.height / img.height;
            const ws = rb.width / img.width;
            if (hs < 1 || ws < 1) {
                if (hs < ws) {
                    this.setScale(Math.trunc(hs * 100) / 100);
                }
                else {
                    this.setScale(Math.trunc(ws * 100) / 100);
                }
            }
        }
        catch (e) {}
    }

    scaleToFit(img: HTMLImageElement | HTMLVideoElement, root: HTMLDivElement): void {
        try {
            const rb = root.getBoundingClientRect();
            const hs = rb.height / img.height;
            const ws = rb.width / img.width;
            if (hs < 1 || ws < 1) {
                if (hs < ws) {
                    this.setScale(Math.trunc(hs * 100) / 100);
                }
                else {
                    this.setScale(Math.trunc(ws * 100) / 100);
                }
            }
            else if (hs > 1 || ws > 1) {
                if (hs < ws) {
                    this.setScale(Math.trunc(hs * 100) / 100);
                }
                else {
                    this.setScale(Math.trunc(ws * 100) / 100);
                }
            }
        }
        catch (e) {}
    }
}
